/* One server-priced pack variant, with a stable fulfillment reference for every magnet. */
(() => {
  'use strict';
  const allowedCounts = new Set([1, 2, 3, 4, 5, 6, 7, 8]);
  const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  class MagnetBuilder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-magnet-config]').textContent);
      this.catalog = [];
      this.selected = [];
      this.limit = 24;
      this.busy = false;
      this.ready = false;
      this.query = normalize(new URLSearchParams(location.search).get('q') || '');
      root.querySelector('[data-design-search]').value = new URLSearchParams(location.search).get('q') || '';
      this.grid = root.querySelector('[data-design-grid]');
      this.message = root.querySelector('[data-pack-message]');
      this.radios = [...root.querySelectorAll('[data-count]')];
      if (!this.radios.some(input => input.checked && !input.disabled)) {
        const firstAvailable = this.radios.find(input => !input.disabled);
        if (firstAvailable) firstAvailable.checked = true;
      }
      root.addEventListener('change', event => {
        if (event.target.matches('[data-count]')) {
          this.message.textContent = '';
          this.render(); // Preserve selections; require explicit removal if the new pack is smaller.
        }
      });
      root.querySelector('[data-design-search]').addEventListener('input', event => {
        this.query = normalize(event.target.value.trim());
        this.limit = 24;
        this.renderGrid();
      });
      root.querySelector('[data-show-more]').addEventListener('click', () => {
        this.limit += 24;
        this.renderGrid();
      });
      root.querySelector('[data-add-pack]').addEventListener('click', () => this.addToCart());
      this.render();
      this.load();
    }
    get pack() {
      const input = this.radios.find(radio => radio.checked && !radio.disabled);
      const count = Number(input?.dataset.count);
      return input && allowedCounts.has(count) ? { id: input.value, count, price: input.dataset.price } : null;
    }
    async load() {
      try {
        const response = await fetch(this.config.catalogUrl);
        if (!response.ok) throw new Error('catalog');
        const data = await response.json();
        if (!Array.isArray(data.designs) || !data.designs.length) throw new Error('empty');
        const ids = new Set();
        this.catalog = data.designs.map(design => {
          if (!/^MAG-[a-f0-9]{12}$/i.test(design.id) || ids.has(design.id) || !design.title || !design.image || !/^https:\/\//.test(design.image)) throw new Error('invalid design');
          ids.add(design.id);
          return { ...design, search: normalize(['magnet magnets ceramic souvenir', design.title, design.label, ...(design.tags || [])].join(' ')) };
        });
        const requestedDesign = new URLSearchParams(location.search).get("design");
        const initialDesign = this.catalog.find(design => design.id === requestedDesign);
        if (initialDesign && this.pack) this.selected.push(initialDesign);
        this.ready = true;
        this.render();
      } catch (error) {
        this.root.querySelector('[data-results-count]').textContent = 'We couldn’t load the designs. Please refresh this page to try again.';
        this.message.textContent = 'Designs must finish loading before you can add a pack.';
      }
    }
    matchingDesigns() {
      const words = this.query.split(/\s+/).filter(Boolean);
      return this.catalog.filter(design => words.every(word => design.search.includes(word)));
    }
    renderGrid() {
      if (!this.ready) return;
      const matches = this.matchingDesigns();
      const fragment = document.createDocumentFragment();
      matches.slice(0, this.limit).forEach(design => {
        const card = document.createElement('article');
        card.className = 'magnet-builder__card';
        const img = document.createElement('img');
        img.className = 'magnet-builder__image'; img.src = design.image; img.alt = design.title;
        img.loading = 'lazy'; img.width = 300; img.height = 300;
        const body = document.createElement('div'); body.className = 'magnet-builder__card-body';
        const title = document.createElement('h3'); title.textContent = design.label || design.title;
        const button = document.createElement('button'); button.type = 'button';
        const chosen = this.selected.filter(item => item.id === design.id).length;
        button.textContent = chosen ? `Add another (${chosen} selected)` : 'Add to my pack';
        button.setAttribute('aria-label', `Add ${design.label || design.title} to your pack`);
        button.disabled = this.busy || !this.pack || this.selected.length >= this.pack.count;
        button.addEventListener('click', () => {
          if (this.busy || !this.pack || this.selected.length >= this.pack.count) return;
          this.selected.push(design); this.message.textContent = ''; this.render();
          // Grid rerenders replace controls. Restore a useful focus target without moving scroll.
          const next = [...this.grid.querySelectorAll('button')].find(item => item.getAttribute('aria-label') === button.getAttribute('aria-label') && !item.disabled);
          if (next) next.focus({preventScroll:true});
          else this.root.querySelector('[data-add-pack]').focus({preventScroll:true});
        });
        body.append(title, button); card.append(img, body); fragment.append(card);
      });
      this.grid.replaceChildren(fragment);
      this.root.querySelector('[data-results-count]').textContent = matches.length ? `${matches.length} design${matches.length === 1 ? '' : 's'}${this.query ? ' found' : ' to explore'}` : 'No designs match. Try a different city, country, or keyword.';
      this.root.querySelector('[data-show-more]').hidden = matches.length <= this.limit;
    }
    render() {
      if (!this.busy) this.radios.forEach(input => {
        if (input.dataset.wasDisabled !== undefined) {
          input.disabled = input.dataset.wasDisabled === 'true';
          delete input.dataset.wasDisabled;
        }
      });
      const pack = this.pack;
      const remaining = pack ? pack.count - this.selected.length : 0;
      this.root.querySelector('[data-pack-progress]').textContent = !pack ? 'This pack is currently unavailable.' : remaining < 0 ? `Remove ${-remaining} design${remaining === -1 ? '' : 's'} to fit this pack.` : `${this.selected.length} of ${pack.count} selected${remaining ? ` · Choose ${remaining} more` : ' · Your pack is ready'}`;
      this.root.querySelector('[data-pack-price]').textContent = pack?.price || '—';
      this.root.querySelector('[data-mobile-summary]').textContent = pack ? `View your pack · ${this.selected.length}/${pack.count} · ${pack.price}` : 'View your pack';
      this.root.querySelector('[data-empty-pack]').hidden = this.selected.length > 0;
      const list = this.root.querySelector('[data-pack-selections]'); list.replaceChildren();
      this.selected.forEach((design, index) => {
        const item = document.createElement('li'); item.className = 'magnet-builder__selection';
        const img = document.createElement('img'); img.src = design.image; img.alt = ''; img.width = 48; img.height = 48;
        const label = document.createElement('span'); label.textContent = `${index + 1}. ${design.label || design.title}`;
        const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = '×'; remove.disabled = this.busy;
        remove.setAttribute('aria-label', `Remove magnet ${index + 1}: ${design.label || design.title}`);
        remove.addEventListener('click', () => {
          if (this.busy) return;
          this.selected.splice(index, 1); this.message.textContent = ''; this.render();
          const target = list.querySelectorAll('button')[Math.min(index, this.selected.length - 1)] || this.root.querySelector('[data-design-search]');
          target.focus({preventScroll:true});
        });
        item.append(img, label, remove); list.append(item);
      });
      const submit = this.root.querySelector('[data-add-pack]');
      submit.disabled = this.busy || !this.ready || !pack || remaining !== 0;
      submit.textContent = this.busy ? 'Adding your pack…' : pack && remaining === 0 ? `Add pack to cart · ${pack.price}` : remaining < 0 ? 'Remove extra designs' : 'Choose your designs';
      this.radios.forEach(input => { if (this.busy) { input.dataset.wasDisabled = String(input.disabled); input.disabled = true; } else if (input.dataset.wasDisabled !== undefined) { input.disabled = input.dataset.wasDisabled === 'true'; delete input.dataset.wasDisabled; } });
      this.renderGrid();
    }
    async addToCart() {
      const pack = this.pack;
      if (this.busy || !this.ready || !pack || this.selected.length !== pack.count) return;
      const properties = { 'Pack size': `${pack.count} magnet${pack.count === 1 ? '' : 's'}` };
      this.selected.forEach((design, index) => { properties[`Magnet ${index + 1}`] = `${design.id} | ${(design.label || design.title).slice(0, 180)}`; });
      this.busy = true; this.message.textContent = ''; this.render();
      try {
        if (!window.SouvenirMagnets) throw Error('Shopping is still loading. Please try again.');
        await window.SouvenirMagnets.add(this.selected.map(design => design.id));
        window.location.assign(this.config.cartUrl);
      } catch (error) {
        this.busy = false; this.render();
        this.message.textContent = error instanceof TypeError || error instanceof SyntaxError ? 'We couldn’t confirm whether the pack was added. Check your cart before trying again.' : error.message;
        this.root.querySelector('[data-cart-link]').hidden = false;
      }
    }
  }
  const mount = () => document.querySelectorAll('[data-magnet-builder]').forEach(root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true'; new MagnetBuilder(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
  document.addEventListener('shopify:section:load', mount);
})();
